/**
 * UploadPreview.tsx — the Tool Upload journey, built from the design, at /share/preview.
 *
 * WHAT THIS IS: the seven frames of design/Tool Upload*.svg as real, navigable UI. It is a PREVIEW —
 * fixtures only, no fetch, no API, no draft persistence — so it renders identically with the backend
 * down and can be reviewed for fidelity on its own. A state switcher at the top reaches every frame.
 *
 * WHAT THIS IS NOT: wired. Every point where real data or a real action belongs carries an @wire()
 * marker. `grep -rn "@wire(" packages/web/src` is the work-list for the page-to-backend skill.
 *
 * WHY A SEPARATE ROUTE rather than rebuilding /share in place: another change was live in
 * packages/web/src/tools/ while this was written (see docs/design-build/tool-upload/STATE.md), and a
 * preview route collides with nothing. Cutting over is one line in router.tsx when the design is signed
 * off and the wiring is done.
 *
 * FRAME MAPPING (docs/design-build/tool-upload/AUDIT.md §frames):
 *   uploads-empty  Tool Upload (1).svg   Your Uploads, nothing shared yet
 *   picker         Tool Upload P1.svg    "Select one of your Automations"
 *   describe       Tool Upload P2.svg    step 1
 *   instructions   Tool Upload P3.svg    step 2
 *   review         Tool Upload P4.svg    step 3
 *   done           Tool Upload P5.svg    step 4, the confirmation
 *   uploads-one    Tool Upload P6.svg    Your Uploads with the published tool
 *
 * DELIBERATE DEVIATIONS, all recorded in the audit with reasons:
 *   - No left sidebar. The frames show profile-page furniture (Profile Picture / Name / Bio) as
 *     unstyled placeholders at negative x, overhanging and clipped. AUDIT decision 1.
 *   - No "Upload from device". No endpoint exists and none is in scope; omitting rather than disabling
 *     also satisfies PC-F14. AUDIT decision 4.
 *   - Three fields the frames never draw but POST /api/tools requires are added and visibly marked.
 *     AUDIT decision 5.
 *   - Empty state, focus rings and hover states have no precedent in the frames; built from the accent
 *     tokens because UP-F46 and PC-NFR01 require them.
 */
import { useState } from 'react';
import { UPLOAD_PREVIEW_STYLES } from './styles';

/** The frames, in journey order. The switcher and the wizard both read this. */
const STATES = [
  'uploads-empty',
  'picker',
  'describe',
  'instructions',
  'review',
  'done',
  'uploads-one',
] as const;
type PreviewState = (typeof STATES)[number];

const STEP_LABELS = [
  'Describe your tool',
  'Detail your instructions',
  'Review your writeup',
  'Publish your tool',
] as const;

/** Which step pill is current in each wizard frame. Step 4 is the confirmation, which the shipped code
 *  models as a terminal phase rather than a stage — AUDIT decision 3. */
const STEP_OF: Partial<Record<PreviewState, number>> = {
  describe: 1,
  instructions: 2,
  review: 3,
  done: 4,
};

/* ── Fixtures ──────────────────────────────────────────────────────────────────────────────────────
 * Every value below is copy transcribed from the frames (docs/design-build/tool-upload/COPY.md), not
 * invented. The long second row is the frame's own truncated example. */

/** @wire(upload.flows): the picker's rows — expects CloudFlowSummary[] from GET /api/flows/mine */
const FLOWS = [
  { id: '1', name: 'Test_1', modified: '2 wk ago', type: 'Scheduled', uploaded: '01/07/2026' },
  {
    id: '2',
    name: 'Test_2 My name is steve and I think that this will be super...',
    modified: '3 mth ago',
    type: 'Scheduled',
    uploaded: '13/04/2026',
  },
  { id: '3', name: 'Test_3', modified: '3 mth ago', type: 'Scheduled', uploaded: '13/04/2026' },
] as const;

/** @wire(upload.identity): read-only builder identity — expects displayName, brand and jobTitle from
 *  GET /api/me. Pre-filled and not editable (UP-F02). */
const IDENTITY = [
  { label: 'Name:', value: 'William_Tidmarsh' },
  { label: 'Brand:', value: 'Volkswagen' },
  { label: 'Job Title:', value: 'Sales Manager' },
] as const;

/** @wire(upload.components): one required explanation per flow action — expects components[] from
 *  POST /api/tools/inspect-components, and the shared isExplained predicate for the gate. The frames
 *  label these "Action {n} Instruction: {connector}*". */
const COMPONENTS = [
  { id: 'a1', label: 'Outlook' },
  { id: 'a2', label: 'Gmail' },
  { id: 'a3', label: 'Click' },
  { id: 'a4', label: 'Save' },
] as const;

/** @wire(upload.preview): the tool-preview art — the frames embed a 1280x960 raster of the real
 *  connector marks, but that art is derived from the selected flow, so it is dynamic. Expects the
 *  connector list from required_connectors on POST /api/tools (or inspect-connectors). Rendered here as
 *  neutral initial tiles rather than fake brand logos. */
const TOOL = { name: 'Test2', by: 'William_Tidmarsh' } as const;

/** @wire(upload.refdata): category, department and access options — expects
 *  GET /api/tools/refdata -> { categories, departments, accessRequirements }. */
const CATEGORIES = ['Automations', 'Apps', 'AI & Agents', 'Spreadsheets'] as const;

function ConnectorTiles({ size }: { size?: 'lg' }) {
  return (
    <>
      {COMPONENTS.map((c) => (
        <span key={c.id} className="tb-up-conn" title={c.label} aria-hidden="true">
          {c.label.slice(0, 1)}
        </span>
      ))}
      {size === 'lg' ? null : null}
    </>
  );
}

function Magnifier() {
  return (
    <svg viewBox="0 0 11 11" fill="none" aria-hidden="true" focusable="false">
      <circle cx="4.4" cy="4.4" r="3.9" stroke="currentColor" />
      <path d="M7.4 7.4L10.5 10.5" stroke="currentColor" strokeLinecap="round" />
    </svg>
  );
}

/** The tool card from P6. The Tool Preview panel in P2/P3 is the same component at a smaller size. */
function ToolCard() {
  return (
    <button type="button" className="tb-up-card">
      <span className="tb-up-card__art">
        <ConnectorTiles />
      </span>
      <span className="tb-up-card__meta">
        <span className="tb-up-card__name">{TOOL.name}</span>
        <span className="tb-up-card__by">{TOOL.by}</span>
      </span>
    </button>
  );
}

/** The left column of P2-P4: read-only identity, then the tool preview. */
function WizardAside() {
  return (
    <aside className="tb-up-aside">
      <h3 className="tb-up-aside__h">Your Identity</h3>
      <dl className="tb-up-ident">
        {IDENTITY.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      <h3 className="tb-up-aside__h">Tool Preview</h3>
      <ToolCard />
    </aside>
  );
}

/** The footer shared by P2-P4: the identity help line, then Cancel and the forward action. */
function WizardFoot({
  onBack,
  onNext,
  nextLabel,
}: {
  onBack: () => void;
  onNext: () => void;
  nextLabel: string;
}) {
  return (
    <div className="tb-up-foot">
      <p className="tb-up-foot__help">
        Are the details in your identity wrong? Get help{' '}
        {/* @wire(upload.identityHelp): destination for the identity-correction route */}
        <a href="/me">here</a>.
      </p>
      <div className="tb-up-foot__end">
        <button type="button" className="tb-up-btn" onClick={onBack}>
          Cancel
        </button>
        <button type="button" className="tb-up-btn" onClick={onNext}>
          {nextLabel}
        </button>
      </div>
    </div>
  );
}

export function UploadPreview() {
  const [state, setState] = useState<PreviewState>('uploads-empty');
  const [selected, setSelected] = useState<string | null>('2');
  const step = STEP_OF[state];
  const inModal = state !== 'uploads-empty' && state !== 'uploads-one';

  return (
    <div className="tb-up">
      <style>{UPLOAD_PREVIEW_STYLES}</style>

      {/* Preview-only. Not part of the design — it exists so every frame is reachable with no backend. */}
      <div className="tb-up-devbar">
        <span>Preview state:</span>
        {STATES.map((s) => (
          <button key={s} type="button" aria-pressed={state === s} onClick={() => setState(s)}>
            {s}
          </button>
        ))}
      </div>

      {/* The page behind the modal (frames 1 and 7). */}
      <div className="tb-up__bar">
        <h1 className="tb-up__title">Your Uploads</h1>
        <button type="button" className="tb-up__publish" onClick={() => setState('picker')}>
          Publish
        </button>
      </div>
      <div className="tb-up__panel">
        {state === 'uploads-one' ? (
          <ToolCard />
        ) : (
          <p className="tb-up__empty">
            Nothing shared yet. Choose <strong>Publish</strong> to share one of your automations.
          </p>
        )}
      </div>

      {inModal ? (
        <div className="tb-up-scrim">
          <div
            className="tb-up-modal"
            role="dialog"
            aria-modal="true"
            aria-label={state === 'picker' ? 'Select one of your Automations' : 'Share a tool'}
          >
            {/* ── P1: the picker ──────────────────────────────────────────────────────────────── */}
            {state === 'picker' ? (
              <div className="tb-up-pick">
                <h2 className="tb-up-pick__h">Select one of your Automations</h2>

                <div className="tb-up-pick__tools">
                  <span className="tb-up-search">
                    <Magnifier />
                    {/* @wire(upload.flowSearch): filters the flow list client-side */}
                    <input
                      type="search"
                      aria-label="Search your automations"
                      placeholder="Search"
                    />
                  </span>
                  {/* @wire(upload.flowFilter): the frame draws a Filter control but no filter UI */}
                  <button type="button" className="tb-up-btn">
                    Filter
                  </button>
                </div>

                <table className="tb-up-table">
                  <thead>
                    <tr>
                      <th className="tb-up-table__pick">
                        <span className="tb-up-sr">Select</span>
                      </th>
                      <th className="tb-up-table__icon" aria-hidden="true" />
                      <th className="tb-up-table__name">Name</th>
                      <th>Modified</th>
                      <th>Type</th>
                      <th>Last Uploaded</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FLOWS.map((f) => (
                      <tr
                        key={f.id}
                        aria-selected={selected === f.id}
                        tabIndex={0}
                        onClick={() => setSelected(f.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelected(f.id);
                          }
                        }}
                      >
                        <td className="tb-up-table__pick">
                          <span className="tb-up-radio" />
                        </td>
                        <td className="tb-up-table__icon">
                          {/* The trigger glyph from the Automations sheet — a clock for a schedule. */}
                          <svg
                            viewBox="0 0 12 12"
                            width="12"
                            height="12"
                            fill="none"
                            aria-hidden="true"
                          >
                            <circle cx="6" cy="6.5" r="4.6" stroke="currentColor" />
                            <path
                              d="M6 4.2V6.5L7.6 7.6"
                              stroke="currentColor"
                              strokeLinecap="round"
                            />
                          </svg>
                        </td>
                        <td className="tb-up-table__name">{f.name}</td>
                        <td>{f.modified}</td>
                        <td>{f.type}</td>
                        <td>{f.uploaded}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="tb-up-pick__foot">
                  {/* @wire(upload.resumeDraft): enabled only when draftStore has a draft for this flow */}
                  <button type="button" className="tb-up-btn">
                    Continue Draft
                  </button>
                  <div className="tb-up-foot__end">
                    <button
                      type="button"
                      className="tb-up-btn"
                      onClick={() => setState('uploads-empty')}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="tb-up-btn"
                      disabled={selected === null}
                      onClick={() => setState('describe')}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* ── P2-P5: the wizard ─────────────────────────────────────────────────────────── */}
                <ol className="tb-up-steps">
                  {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    return (
                      <li
                        key={label}
                        aria-current={step === n ? 'step' : undefined}
                        data-done={step !== undefined && n < step}
                      >
                        <span>{n}</span>
                        {label}
                      </li>
                    );
                  })}
                </ol>

                {state === 'done' ? (
                  <>
                    <div className="tb-up-done">
                      <p>Your Tool&rsquo;s found its seat on the Kombi. Congratulations!</p>
                    </div>
                    <div className="tb-up-main" style={{ paddingTop: 0 }}>
                      <div className="tb-up-foot">
                        <div className="tb-up-foot__end">
                          <button
                            type="button"
                            className="tb-up-btn"
                            onClick={() => setState('uploads-one')}
                          >
                            Finish
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="tb-up-body">
                    {state === 'review' ? (
                      <aside className="tb-up-aside">
                        <div className="tb-up-review__art">
                          <div>
                            <ConnectorTiles size="lg" />
                          </div>
                        </div>
                        <p className="tb-up-card__name" style={{ marginTop: 12 }}>
                          {TOOL.name}
                        </p>
                        <p className="tb-up-card__by" style={{ margin: 0 }}>
                          {TOOL.by}
                        </p>
                      </aside>
                    ) : (
                      <WizardAside />
                    )}

                    <div className="tb-up-main">
                      {state === 'describe' ? (
                        <div className="tb-up-fields">
                          <div className="tb-up-row">
                            <label className="tb-up-field">
                              <span>Tool Name*</span>
                              {/* @wire(upload.details.name): 3-80 chars, mirrors LIMITS in
                                  upload/validation.ts; triggers the duplicate check */}
                              <input type="text" />
                            </label>
                            <label className="tb-up-field">
                              <span>Primary Category*</span>
                              {/* @wire(upload.details.category): exactly one of refdata.categories */}
                              <select defaultValue="">
                                <option value="" disabled />
                                {CATEGORIES.map((c) => (
                                  <option key={c}>{c}</option>
                                ))}
                              </select>
                            </label>
                          </div>

                          <label className="tb-up-field">
                            <span>Tool Description*</span>
                            {/* @wire(upload.details.description): 10-400 chars */}
                            <textarea />
                          </label>

                          <label className="tb-up-field">
                            <span>Tags*</span>
                            {/* @wire(upload.details.tags): up to 5, normalised; the 5-tag cap is a DB
                                trigger, so the client cap must match CAT-F12 */}
                            <input type="text" />
                          </label>

                          <div className="tb-up-fields__half">
                            <label className="tb-up-field">
                              <span>Time Saved*</span>
                              {/* @wire(upload.details.timeSaved): whole minutes, 1-600 */}
                              <input type="number" min={1} max={600} />
                            </label>
                          </div>

                          {/* The three below are NOT in any frame. POST /api/tools requires all three,
                              so the form cannot submit without them — AUDIT decision 5. Marked in the
                              UI so the addition is visible rather than blended in. */}
                          <div className="tb-up-row">
                            <label className="tb-up-field" data-added="true">
                              <span>Department</span>
                              {/* @wire(upload.details.department): refdata.departments, defaults to the
                                  builder's own but editable (UP-F05) */}
                              <select defaultValue="" />
                            </label>
                            <label className="tb-up-field" data-added="true">
                              <span>Who may use it</span>
                              {/* @wire(upload.details.access): refdata.accessRequirements, defaults to
                                  All employees (UP-F55) */}
                              <select defaultValue="" />
                            </label>
                          </div>
                          <label className="tb-up-field" data-added="true">
                            <span>Dependencies</span>
                            {/* @wire(upload.details.dependencies): required, non-empty (UP-F16) */}
                            <textarea />
                          </label>
                        </div>
                      ) : null}

                      {state === 'instructions' ? (
                        <div className="tb-up-fields">
                          {COMPONENTS.map((c, i) => (
                            <label className="tb-up-field" key={c.id}>
                              <span>
                                Action {i + 1} Instruction: {c.label}*
                              </span>
                              {/* @wire(upload.instructions): one required explanation per component;
                                  the gate is isExplained from @trailblazer/shared, which the SERVER
                                  also uses, so the two cannot drift */}
                              <textarea />
                            </label>
                          ))}
                        </div>
                      ) : null}

                      {state === 'review' ? (
                        <div className="tb-up-fields">
                          <h3 className="tb-up-review__h">Details</h3>
                          <div className="tb-up-row">
                            <label className="tb-up-field">
                              <span>Tool Name*</span>
                              <input type="text" />
                            </label>
                            <label className="tb-up-field">
                              <span>Primary Category*</span>
                              <select defaultValue="" />
                            </label>
                          </div>
                          <label className="tb-up-field">
                            <span>Tool Description*</span>
                            <textarea />
                          </label>
                          <label className="tb-up-field">
                            <span>Tags*</span>
                            <input type="text" />
                          </label>
                          <div className="tb-up-fields__half">
                            <label className="tb-up-field">
                              <span>Time Saved*</span>
                              <input type="number" />
                            </label>
                          </div>
                          <h3 className="tb-up-review__h">Instructions</h3>
                          {COMPONENTS.map((c, i) => (
                            <label className="tb-up-field" key={c.id}>
                              <span>
                                Action {i + 1} Instruction: {c.label}*
                              </span>
                              <textarea />
                            </label>
                          ))}
                        </div>
                      ) : null}

                      <WizardFoot
                        onBack={() => setState(state === 'describe' ? 'picker' : 'describe')}
                        onNext={() =>
                          setState(
                            state === 'describe'
                              ? 'instructions'
                              : state === 'instructions'
                                ? 'review'
                                : 'done',
                          )
                        }
                        nextLabel="Next"
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
